import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

interface DashboardStats {
  totalSales: number;
  salesGrowth: number;
  totalOrders: number;
  ordersGrowth: number;
  totalProducts: number;
  lowStock: number;
  totalUsers: number;
  activeUsers: number;
  advanceOrders: number;
  pendingAdvanceOrders: number;
  totalAdvanceAmount: number;
}

interface RecentActivity {
  type: 'sale' | 'inventory' | 'user' | 'system';
  description: string;
  time: string;
  user: string;
  created_at: string;
}

export const useDashboardStats = () => {
  const { selectedCompany, user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalSales: 0,
    salesGrowth: 0,
    totalOrders: 0,
    ordersGrowth: 0,
    totalProducts: 0,
    lowStock: 0,
    totalUsers: 0,
    activeUsers: 0,
    advanceOrders: 0,
    pendingAdvanceOrders: 0,
    totalAdvanceAmount: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  const loadDashboardStats = async () => {
    if (!selectedCompany?.id || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // 1. Obtener sesión activa del usuario para usar su operational_date
      const { data: activeSession } = await supabase
        .from('cash_register_sessions')
        .select('operational_date, session_date')
        .eq('company_id', selectedCompany.id)
        .eq('user_id', user.id)
        .is('closed_at', null)
        .order('opened_at', { ascending: false })
        .limit(1)
        .single();

      // 2. Determinar si usar fecha operativa o fecha actual
      let useOperationalDate = false;
      let targetDate: string | null = null;
      
      if (activeSession?.operational_date) {
        useOperationalDate = true;
        targetDate = activeSession.operational_date;
        console.log('📅 Dashboard Stats - Usando fecha operativa simulada:', targetDate);
      } else {
        console.log('📅 Dashboard Stats - Usando fechas reales del sistema');
      }

      // Get sales stats (last 30 days o basado en operational_date)
      const thirtyDaysAgo = new Date();
      if (useOperationalDate && targetDate) {
        // Si hay fecha operativa, calcular 30 días desde esa fecha
        const opDate = new Date(targetDate + 'T12:00:00');
        opDate.setDate(opDate.getDate() - 30);
        thirtyDaysAgo.setTime(opDate.getTime());
      } else {
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      }
      
      const { data: salesData, error: salesError } = await supabase
        .from('sales')
        .select('total_amount, sale_date')
        .eq('company_id', selectedCompany.id)
        .in('status', ['completed', 'certified'])
        .gte('sale_date', thirtyDaysAgo.toISOString().split('T')[0]);

      if (salesError) throw salesError;

      // Calculate sales stats
      const totalSales = salesData?.reduce((sum, sale) => sum + (sale.total_amount || 0), 0) || 0;
      const totalOrders = salesData?.length || 0;

      // Get previous month data for growth calculation
      const sixtyDaysAgo = new Date();
      if (useOperationalDate && targetDate) {
        const opDate60 = new Date(targetDate + 'T12:00:00');
        opDate60.setDate(opDate60.getDate() - 60);
        sixtyDaysAgo.setTime(opDate60.getTime());
      } else {
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      }

      const { data: prevSalesData } = await supabase
        .from('sales')
        .select('total_amount')
        .eq('company_id', selectedCompany.id)
        .in('status', ['completed', 'certified'])
        .gte('sale_date', sixtyDaysAgo.toISOString().split('T')[0])
        .lt('sale_date', thirtyDaysAgo.toISOString().split('T')[0]);

      const prevTotalSales = prevSalesData?.reduce((sum, sale) => sum + (sale.total_amount || 0), 0) || 0;
      const prevTotalOrders = prevSalesData?.length || 0;

      const salesGrowth = prevTotalSales > 0 ? ((totalSales - prevTotalSales) / prevTotalSales) * 100 : 0;
      const ordersGrowth = prevTotalOrders > 0 ? ((totalOrders - prevTotalOrders) / prevTotalOrders) * 100 : 0;

      // Get products stats - Count total products first
      const { count: totalProductsCount, error: countError } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true);

      if (countError) throw countError;

      // Get products data for stock analysis
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, current_stock, min_stock')
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true);

      if (productsError) throw productsError;

      const totalProducts = totalProductsCount || 0;

      // Compute low stock using product-level stock as reliable source
      const defaultMinThreshold = 5;
      const lowStock =
        productsData?.filter(p => {
          const min = p.min_stock && p.min_stock > 0 ? p.min_stock : defaultMinThreshold;
          return p.current_stock > 0 && p.current_stock <= min;
        }).length || 0;

      // Get users stats
      const { data: usersData, error: usersError } = await supabase
        .from('user_roles')
        .select('user_id, is_active')
        .eq('company_id', selectedCompany.id);

      if (usersError) throw usersError;

      const totalUsers = usersData?.length || 0;
      const activeUsers = usersData?.filter(u => u.is_active).length || 0;

      // Get advance orders (if B2B orders exist)
      const { data: advanceOrdersData } = await supabase
        .from('b2b_orders')
        .select('id, total_amount, status')
        .eq('company_id', selectedCompany.id)
        .eq('payment_status', 'partial');

      const advanceOrders = advanceOrdersData?.length || 0;
      const pendingAdvanceOrders = advanceOrdersData?.filter(o => o.status === 'pending').length || 0;
      const totalAdvanceAmount = advanceOrdersData?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;

      setStats({
        totalSales,
        salesGrowth,
        totalOrders,
        ordersGrowth,
        totalProducts,
        lowStock,
        totalUsers,
        activeUsers,
        advanceOrders,
        pendingAdvanceOrders,
        totalAdvanceAmount,
      });

      // Get recent activity from audit log
      const { data: activityData } = await supabase
        .from('audit_log')
        .select('operation, details, created_at, user_id')
        .eq('company_id', selectedCompany.id)
        .order('created_at', { ascending: false })
        .limit(10);

      const activities: RecentActivity[] = activityData?.map(activity => {
        const timeAgo = getTimeAgo(new Date(activity.created_at));
        
        return {
          type: getActivityType(activity.operation),
          description: getActivityDescription(activity.operation, activity.details),
          time: timeAgo,
          user: 'Usuario',
          created_at: activity.created_at
        };
      }).slice(0, 4) || [];

      setRecentActivity(activities);

    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // Keep current state on error - no fallback data
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardStats();
  }, [selectedCompany?.id, user?.id]);

  return {
    stats,
    recentActivity,
    loading,
    refetch: loadDashboardStats
  };
};

// Helper functions
const getTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'ahora';
  if (diffInMinutes < 60) return `${diffInMinutes} min`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  return `${Math.floor(diffInMinutes / 1440)}d`;
};

const getActivityType = (operation: string): RecentActivity['type'] => {
  if (operation.includes('sale') || operation.includes('invoice')) return 'sale';
  if (operation.includes('product') || operation.includes('stock')) return 'inventory';
  if (operation.includes('user')) return 'user';
  return 'system';
};

const getActivityDescription = (operation: string, details: any): string => {
  switch (operation) {
    case 'INSERT':
      if (details?.table_name === 'sales') return `Nueva venta registrada`;
      if (details?.table_name === 'products') return `Nuevo producto añadido`;
      if (details?.table_name === 'users') return `Nuevo usuario registrado`;
      break;
    case 'UPDATE':
      if (details?.table_name === 'products') return `Producto actualizado`;
      if (details?.table_name === 'sales') return `Venta modificada`;
      break;
    case 'DELETE':
      if (details?.table_name === 'sales') return `Venta eliminada`;
      break;
    default:
      return `Operación del sistema: ${operation}`;
  }
  
  return `Actividad del sistema`;
};